import { Box, CheckBox, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Tooltip } from '@factorialco/f0-react/dist/experimental'
import { InfoCircleLine } from '@factorialco/f0-react/icons/app'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import i18n from 'modules/core/lib/i18n'

import type DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import type DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'
import type ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import ShiftsEditForm from 'modules/shift_management/lib/forms/ShiftsEditForm'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'

// Helper function to get tooltip content for F0 Tooltip
const getOvertimeTooltipContent = (isComplementaryHoursEnabled: boolean) => {
  if (isComplementaryHoursEnabled) {
    return {
      label: i18n.t('shift_management.add_shift.overtime_tooltip_label'),
      description: `${i18n.t('shift_management.add_shift.new_first_overtime_tooltip')}\n\n${i18n.t(
        'shift_management.add_shift.new_second_overtime_tooltip'
      )}`,
    }
  }
  return {
    label: '',
    description: undefined,
  }
}

// Helper function to render tooltip content for InformationTooltip (JSX)
const renderOvertimeTooltipContent = () => {
  const newFirstOvertimeTooltipInfo = i18n.t(
    'shift_management.add_shift.new_first_overtime_tooltip'
  )
  const newSecondOvertimeTooltipInfo = i18n.t(
    'shift_management.add_shift.new_second_overtime_tooltip'
  )

  return (
    <Box textAlign="left">
      <Box paddingBottom="s8">
        <Text.Normal size="200">{newFirstOvertimeTooltipInfo}</Text.Normal>
      </Box>
      <Text.Normal size="200">{newSecondOvertimeTooltipInfo}</Text.Normal>
    </Box>
  )
}

export interface Props {
  form:
    | DefaultShiftWithParamsForm
    | ShiftForm
    | UnassignedShiftForm
    | ShiftsEditForm
    | DefaultShiftForm
  newVersion?: boolean
  isComplementaryHoursEnabled: boolean
}

const ShiftOvertimeCheckbox = ({
  form,
  isComplementaryHoursEnabled,
  newVersion = false,
}: Props) => {
  const checkboxInfo = i18n.t('shift_management.add_shift.overtime')

  const isEditForm = form instanceof ShiftsEditForm
  const indeterminate = isEditForm && form.values.extraHours === ''

  const oldTooltipInfo = indeterminate
    ? i18n.t('shift_management.edit_shifts.extra_hours_info')
    : i18n.t('shift_management.add_shift.overtime_tooltip')

  const onChange = () => {
    if (indeterminate) {
      form.setValues({
        extraHours: false,
      })
    } else if (isEditForm) {
      form.setValues({
        extraHours: !form.values.extraHours,
      })
    } else {
      form.setValues({
        extra_hours: !form.values.extra_hours,
      })
    }
  }

  const checked = isEditForm ? form.values.extraHours : form.values.extra_hours

  const tooltipContent = getOvertimeTooltipContent(isComplementaryHoursEnabled)
  const tooltipLabel = isComplementaryHoursEnabled ? tooltipContent.label : oldTooltipInfo
  const tooltipDescription = isComplementaryHoursEnabled ? tooltipContent.description : undefined

  return (
    <Box
      flexDirection="row"
      gap={newVersion ? 's4' : 's8'}
      alignItems="center"
      paddingLeft={newVersion ? 's40' : undefined}
    >
      <Box flexDirection="row" gap="s8" alignItems="center">
        <CheckBox indeterminate={indeterminate} checked={checked} onClick={() => onChange()} />
        <Text.Normal size="200" weight="medium">
          {checkboxInfo}
        </Text.Normal>
      </Box>
      {newVersion ? (
        <Tooltip label={tooltipLabel} description={tooltipDescription}>
          <F0Icon icon={InfoCircleLine} size="md" color="grey800" />
        </Tooltip>
      ) : (
        <Box>
          <InformationTooltip
            information={
              isComplementaryHoursEnabled ? renderOvertimeTooltipContent() : oldTooltipInfo
            }
            icon="question"
          />
        </Box>
      )}
    </Box>
  )
}

export default observer(ShiftOvertimeCheckbox)
